import { HEX } from '@somnhold/content';
import { iatan2, idiv, imax, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, KindleRules, KindleState, SendaNpc } from '../types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { guardTick } from './keep.ts';

/**
 * The second altar (docs/sendas/paladin.md, chapter 1, act 2): at the foot of the first Vigil's watchtower, among its
 * molten ballistas, the altar is held while it lights (the fight's `time` goal). Crías and soot crows come at it and at
 * Ser Marit, who fights beside it, slow, sword and shield (battle/keep.ts). The glass drake comes in at `in` and keeps
 * off at `hover`; at `at` and every `every` after it sweeps a cone of fire at Marit, marked `warn` before: `bp` of the
 * life of all it catches. Marit can't run out of it («¡Ayúdeme a salir, que no corro!»): a hero within `cover` of her as
 * it sweeps takes it for her, the paladin's own way of standing in the middle. Lost with the altar out (its blows, a
 * prop of the path, battle/senda.ts), Marit or the hero down.
 *
 * Cost: the cone looks at the hero and Marit once a sweep; the drake moves by this rule only.
 */

const fresh = (r: KindleRules): KindleState => ({ drake: 0, next: r.drake.at, aim: 0, marked: false, sweeps: 0, covered: 0, swing: 0 });

const maritOf = (s: BattleState): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === 'marit');

/** Whether a point lies in the cone from (x, y) along `aim` (1024 a turn), `range` long and `arc` either side. */
export const inCone = (x: number, y: number, aim: number, range: number, arc: number, px: number, py: number): boolean => {
  if (!withinRange(x, y, px, py, range)) return false;
  if (px === x && py === y) return true;
  const a = iatan2(py - y, px - x);
  let d = (((a - aim) % 1024) + 1024) % 1024;
  if (d > 512) d = 1024 - d;
  return d <= arc;
};

/** The drake's own way: off at `hover` from Marit, on her far side from the hero, facing her. */
export const kindleHold = (s: BattleState, e: Enemy, pace: (e: Enemy) => number): boolean => {
  const r = s.rules?.senda?.kindle;
  const w = s.senda?.kindle;
  if (!r || !w || e.id !== w.drake || e.stun > 0) return false;
  if (e.cd > 0) e.cd--;
  const m = maritOf(s);
  const c = m ?? r.keep;
  if (w.marked) {
    e.anim = 'cast';
    return true;
  }
  // Where it hangs: north of what it watches, `hover` off.
  const tx = c.x;
  const ty = c.y - r.drake.hover;
  e.facing = iatan2(c.y - e.y, c.x - e.x);
  if (!withinRange(e.x, e.y, tx, ty, 300)) {
    e.anim = 'walk';
    stepToward(e, tx, ty, pace(e));
  } else e.anim = 'idle';
  return true;
};

export const kindleTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.kindle;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.kindle ??= fresh(r));
  if (!s.started) return;
  const D = r.drake;
  const T = s.waveT;
  const m = maritOf(s);
  if (m) w.swing = guardTick(s, m, r.keep, r.marit, w.swing, (e) => e.id === w.drake);
  if (!w.drake && T >= D.in) {
    const e = spawnEnemy(s, 'glassDrake', 1, false, 0, r.keep.x + 2 * HEX, r.keep.y - 7 * HEX, D.hp);
    w.drake = e.id;
    cue('drake');
  }
  const drake = w.drake ? s.enemies.find((e) => e.id === w.drake && e.hp > 0) : undefined;
  if (!drake) return;
  if (!w.marked && T >= w.next - D.warn) {
    // The cone marked at Marit (or where she keeps), from where it hangs.
    const t = m && m.hp > 0 ? m : r.keep;
    w.aim = iatan2(t.y - drake.y, t.x - drake.x);
    w.marked = true;
    drake.facing = w.aim;
    ev(s, { t: 'senda', kind: 'coneWarn', npc: '', x: drake.x, y: drake.y, n: w.aim });
    if (m && m.hp > 0 && inCone(drake.x, drake.y, w.aim, D.range, D.arc, m.x, m.y)) cue('cone');
  }
  if (w.marked && T >= w.next) {
    w.marked = false;
    w.next = T + D.every;
    w.sweeps++;
    ev(s, { t: 'senda', kind: 'cone', npc: '', x: drake.x, y: drake.y, n: w.aim });
    let cover = false;
    for (const h of s.heroes) {
      if (h.downTicks > 0) continue;
      if (inCone(drake.x, drake.y, w.aim, D.range, D.arc, h.x, h.y)) damageHero(s, h, mulBp(h.stats.maxHp, D.bp), drake, 'fire', true);
      if (m && withinRange(h.x, h.y, m.x, m.y, D.cover)) cover = true;
    }
    if (m && m.hp > 0 && inCone(drake.x, drake.y, w.aim, D.range, D.arc, m.x, m.y)) {
      if (cover) {
        w.covered++;
        ev(s, { t: 'senda', kind: 'cover', npc: m.id, x: m.x, y: m.y });
      } else {
        m.hp -= imax(1, idiv(m.maxHp * D.bp + 9999, 10000));
        if (m.hp < 0) m.hp = 0;
        ev(s, { t: 'senda', kind: m.hp > 0 ? 'hit' : 'down', npc: m.id, x: m.x, y: m.y });
      }
    }
  }
};

/** The cone marked (for the renderer and the HUD): from where, along what, how long and how wide; null while none is. */
export const kindleCone = (s: BattleState): { x: number; y: number; aim: number; range: number; arc: number } | null => {
  const r = s.rules?.senda?.kindle;
  const w = s.senda?.kindle;
  if (!r || !w || !w.marked) return null;
  const d = s.enemies.find((e) => e.id === w.drake);
  if (!d) return null;
  return { x: d.x, y: d.y, aim: w.aim, range: r.drake.range, arc: r.drake.arc };
};
